import React, { useState } from 'react';
import {
  ChevronDown, ChevronUp, CircleCheck, Columns3, EyeOff, Rows3, Target,
} from 'lucide-react';
import Block from './Block';
import Disclosure from './Disclosure';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

/**
 * Renders a `dataset_summary` block. Backend payload is a DatasetCard:
 *   { dataset_id, n_rows, n_cols, columns: [{name, dtype, ...}],
 *     dropped_columns, remaining_columns, original_columns, warnings, ... }
 */

const DEFAULT_VISIBLE = 9;

// Why a column ended up where it did, keyed by the backend's `column_roles`.
// Only roles that carry an explanation get a label — an ordinary source column
// needs none, and labelling every chip would drown out the ones that matter.
// The label sits inside the column's own chip (like the target's), so a role
// never reads as a separate column.
const ROLE_BADGES = {
  // A correctness risk the user should notice: the whole chip turns red.
  leakage: { label: 'leakage', variant: 'destructive' },
  // Not a problem, just not a feature — kept for joins and per-instance tracking.
  identifier: { label: 'identifier', variant: 'secondary' },
  // Built during preprocessing rather than handed to us.
  engineered: { label: 'engineered', variant: 'secondary' },
};

const ColumnChips = ({ names, target = null, roles = {} }) => (
  <>
    {names.map((name) => {
      const isTarget = target && name === target;
      const role = !isTarget ? ROLE_BADGES[roles[name]] : null;
      if (isTarget) {
        return (
          <Badge key={name} variant="secondary" title="Target column — what the model predicts">
            <Target aria-hidden />
            {name}
            <span className="q-muted" style={{ fontWeight: 400 }}>target</span>
          </Badge>
        );
      }
      return (
        <Badge key={name} variant={role ? role.variant : 'secondary'}>
          {name}
          {role && <span style={{ fontWeight: 400, opacity: 0.7 }}>{role.label}</span>}
        </Badge>
      );
    })}
  </>
);

const DatasetSummary = ({ blockData }) => {
  const [keptExpanded, setKeptExpanded] = useState(false);
  const [droppedExpanded, setDroppedExpanded] = useState(false);

  if (!blockData) return null;

  const rows = blockData.n_rows ?? 0;
  const columns = Array.isArray(blockData.columns) ? blockData.columns : [];
  const totalCols = blockData.n_cols ?? columns.length;
  const dropped = Array.isArray(blockData.dropped_columns) ? blockData.dropped_columns : [];
  const target = blockData.target_column || null;
  // Absent on blocks rendered before roles existed — chips then simply carry
  // no bar, which is the same as "no role", so old cards degrade cleanly.
  const roles = (blockData.column_roles && typeof blockData.column_roles === 'object')
    ? blockData.column_roles
    : {};

  // Prefer the explicit remaining_columns list (post PII / datetime pruning);
  // fall back to deriving it from columns minus dropped so this works on
  // older payloads.
  const droppedSet = new Set(dropped);
  const explicitRemaining = Array.isArray(blockData.remaining_columns) && blockData.remaining_columns.length > 0
    ? blockData.remaining_columns
    : null;
  const kept = explicitRemaining || columns.map(c => c.name).filter(n => n && !droppedSet.has(n));

  // The target stays in the dataset (so it appears as a chip, badged) but is not
  // a predictor, so it must not be counted as a feature.
  const featureCount = target ? kept.filter(n => n !== target).length : kept.length;

  // Show the target chip first (badged) so it's never hidden behind the fold,
  // followed by the actual feature columns.
  const orderedKept = target && kept.includes(target)
    ? [target, ...kept.filter(n => n !== target)]
    : kept;
  const visibleKept = keptExpanded ? orderedKept : orderedKept.slice(0, DEFAULT_VISIBLE);
  const hiddenKeptCount = Math.max(0, orderedKept.length - DEFAULT_VISIBLE);

  const stats = [
    { icon: Rows3, label: 'Rows', value: Number(rows).toLocaleString() },
    { icon: Columns3, label: 'Features', value: featureCount.toLocaleString() },
    { icon: EyeOff, label: 'Excluded', value: (dropped.length || 0).toLocaleString() },
  ];
  const originalCols = totalCols > kept.length + dropped.length ? totalCols : null;

  return (
    <Block
      icon="file-spreadsheet"
      title={blockData.dataset_name || blockData.dataset_id || 'Dataset'}
      description={originalCols
        ? `Ready for modeling · ${originalCols} columns in the original file`
        : 'Ready for modeling'}
      badge={<Badge variant="success"><CircleCheck aria-hidden />Complete</Badge>}
    >
      <div className="q-stats">
        {stats.map(({ icon: StatIcon, label, value }) => (
          <div key={label} className="q-stat">
            <span className="q-stat__label"><StatIcon aria-hidden />{label}</span>
            <span className="q-stat__value">{value}</span>
          </div>
        ))}
      </div>

      <div className="q-section" style={{ marginTop: 24 }}>
        <div className="q-section__head" style={{ minHeight: 0 }}>
          <span className="q-section__title">Columns</span>
          <Badge variant="secondary" className="ui-badge--mono">{orderedKept.length}</Badge>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <ColumnChips names={visibleKept} target={target} roles={roles} />
          {hiddenKeptCount > 0 && (
            <Button
              variant="ghost"
              size="xs"
              aria-expanded={keptExpanded}
              onClick={() => setKeptExpanded((v) => !v)}
            >
              {keptExpanded ? 'Show less' : `+${hiddenKeptCount} more`}
              {keptExpanded ? <ChevronUp aria-hidden /> : <ChevronDown aria-hidden />}
            </Button>
          )}
        </div>
      </div>

      {dropped.length > 0 && (
        <Disclosure
          className="q-disclosure--spaced"
          label={`Excluded ${dropped.length} column${dropped.length === 1 ? '' : 's'} from training`}
          open={droppedExpanded}
          onOpenChange={setDroppedExpanded}
        >
          <div className="q-disclosure__content--indent" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <ColumnChips names={dropped} roles={roles} />
          </div>
        </Disclosure>
      )}
    </Block>
  );
};

export default DatasetSummary;
